import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditAddressModal from '@modals/CreateOrEditAddressModal'
import CreateOrEditAgencyContactModal from '@modals/CreateOrEditAgencyContactModal'
import CreateOrEditAgencyModal from '@modals/CreateOrEditAgencyModal'
import CreateOrEditAgencyPaymentMethodModal from '@modals/CreateOrEditAgencyPaymentMethodModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import ManageEntityImagesModal from '@modals/ManageEntityImagesModal'

import type {
  AddressModel, AgencyContactModel, AgencyPaymentMethodModel, ImageModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAddressModal: false,
  showCreateOrEditAgencyContactModal: false,
  showCreateOrEditAgencyModal: false,
  showCreateOrEditAgencyPaymentMethodModal: false,
  showCreateOrEditImageModal: false,
  showManageEntityImagesModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAddressModal: {
      closeModal: () => setState({ showCreateOrEditAddressModal: false }),
      createAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setDefaultAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyContactModal: {
      closeModal: () => setState({ showCreateOrEditAgencyContactModal: false }),
      createAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyModal: {
      closeModal: () => setState({ showCreateOrEditAgencyModal: false }),
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyPaymentMethodModal: {
      closeModal: () => setState({ showCreateOrEditAgencyPaymentMethodModal: false }),
      createAgencyPaymentMethod: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyPaymentMethod: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyPaymentMethod: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageEntityImagesModal: {
      closeModal: () => setState({ showManageEntityImagesModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditAddressModal,
    showCreateOrEditAgencyContactModal,
    showCreateOrEditAgencyModal,
    showCreateOrEditAgencyPaymentMethodModal,
    showCreateOrEditImageModal,
    showManageEntityImagesModal,
  } = state

  const { agencyId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditAddressModal: (payload: {address: AddressModel}) => {
        setModalData('CreateOrEditAddressModal', payload)
        setState({ showCreateOrEditAddressModal: true })
      },
      showCreateOrEditAgencyContactModal: (payload: { agencyContact: AgencyContactModel }) => {
        setModalData('CreateOrEditAgencyContactModal', payload)
        setState({ showCreateOrEditAgencyContactModal: true })
      },
      showCreateOrEditAgencyPaymentMethodModal: (payload: { agencyPaymentMethod: AgencyPaymentMethodModel }) => {
        setModalData('CreateOrEditAgencyPaymentMethodModal', payload)
        setState({ showCreateOrEditAgencyPaymentMethodModal: true })
      },
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showManageEntityImagesModal: (payload: { imageOptions: Partial<ImageModel> }) => {
        setModalData('ManageEntityImagesModal', payload)
        setState({ showManageEntityImagesModal: true })
      },
      updateAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      resetDefaultAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agencyId={agencyId} />

        <CreateOrEditAddressModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAddressModal', setState)}
          showModal={showCreateOrEditAddressModal}
        />

        <CreateOrEditAgencyContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyContactModal', setState)}
          showModal={showCreateOrEditAgencyContactModal}
        />

        <CreateOrEditAgencyModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyModal', setState)}
          showModal={showCreateOrEditAgencyModal}
        />

        <CreateOrEditAgencyPaymentMethodModal
          callbacks={callbacks('CreateOrEditAgencyPaymentMethodModal', setState)}
          showModal={showCreateOrEditAgencyPaymentMethodModal}
        />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <ManageEntityImagesModal
          callbacks={callbacks('ManageEntityImagesModal', setState)}
          showModal={showManageEntityImagesModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
